import type { MaterialInfo, PartDefinition } from "@rogue/rackbuilder"
import { getRackModel, ATTACHMENTS } from "@/js/pages/product/rackbuilder/modelPaths"
import type { RackOptions } from "@/js/pages/product/rackbuilder/RackTypes"

// Rhino Parts — Part definitions for Rhino Rack Trainer (rhinoRackTrainer=true) and Rhino FTX Functional Trainer (rhinoRackTrainer=false)

// MARK: Context
export interface RhinoContext {
  config: RackOptions
  materials: MaterialInfo
  height: RackOptions['height']
  depth: string
}

// MARK: Footplate Assembly
export function createRhinoFootplate(context: RhinoContext, latBarClip: PartDefinition): PartDefinition {
  const back = (context.config.rhinoRackTrainer || context.config.addIndy)
    ? createRackTrainerBack(context, latBarClip)
    : createFTXBack(context, latBarClip)
  const treadPlate = createRhinoTreadPlate(context)
  const lowRow = createRhinoLowRow()

  return {
    mesh  : getRackModel('RHINO_PARTS', 'footplate'),
    slots : {
      plate  : treadPlate,
      lowrow : lowRow,
      back,
    },
  }
}

// MARK: Rack Trainer Back Assembly — used when config.rhinoRackTrainer === true
function createRackTrainerBack(context: RhinoContext, latBarClip: PartDefinition): PartDefinition {
  const { materials, height, depth } = context

  const latPulldown: PartDefinition = {
    mesh      : getRackModel('FM_SHARED', 'lat pulldown'),
    materials : { ...materials, pulley: { material: 'stainless' } },
    slots     : { attachment_clip: latBarClip },
  }

  const topBeam: PartDefinition = {
    mesh  : getRackModel('RHINO_PARTS', `top beam rack trainer ${depth}`),
    slots : { front: latPulldown },
  }

  const topBracket: PartDefinition = {
    mesh  : getRackModel('RHINO_PARTS', 'top bracket rack trainer'),
    slots : { beam: topBeam },
  }

  // TODO: Trainer cables are not depth-aware. On a 4-post Rhino addon at 43" depth,
  // the top bracket uses 'top beam 43' but the cable mesh has no depth variant, causing
  // a visible gap at 90" and 100" heights. Fix requires new models:
  // 'rhino 90 trainer cables 43' and 'rhino 100 trainer cables 43' in FM_CABLES.
  // Once models exist, change to: `rhino ${height} trainer cables ${depth}`
  const trainerCables: PartDefinition = {
    mesh  : getRackModel('FM_CABLES', `rhino ${height} trainer cables`),
    slots : {},
  }

  const backPost: PartDefinition = {
    mesh  : getRackModel('FM', `back post ${height}`),
    slots : {
      cables : trainerCables,
      top    : topBracket,
    },
  }

  const bottomCableLock: PartDefinition = {
    mesh  : getRackModel('RHINO_PARTS', 'bottom cablelock rack trainer'),
    slots : {},
  }

  return {
    mesh  : getRackModel('FM_SHARED', 'bottom'),
    slots : {
      pulley : bottomCableLock,
      beam   : backPost,
    },
  }
}

// MARK: FTX Functional Trainer Back Assembly — used when config.rhinoRackTrainer === false
function createFTXBack(context: RhinoContext, latBarClip: PartDefinition): PartDefinition {
  const { materials, height } = context

  const latPulldown: PartDefinition = {
    mesh      : getRackModel('FM_SHARED', 'lat pulldown'),
    materials : { ...materials, pulley: { material: 'stainless' } },
    slots     : { attachment_clip: latBarClip },
  }

  const top: PartDefinition[] = [
    {
      mesh  : getRackModel('FM_SHARED', 'top ftx'),
      slots : { front: latPulldown },
    },
    { mesh: getRackModel('RHINO_PARTS', 'top back pulley'), slots: {} },
    { mesh: getRackModel('RHINO_PARTS', 'brand plate'), slots: {} },
  ]

  const ftxCables: PartDefinition = {
    mesh  : getRackModel('FM_CABLES', `rhino ${height} ftx cables`),
    slots : {},
  }

  const backPost: PartDefinition = {
    mesh  : getRackModel('RHINO_PARTS', `beam ${height}`),
    slots : {
      cables: ftxCables,
      top,
    },
  }

  const backPulley: PartDefinition = {
    mesh  : getRackModel('FM_SHARED', 'back pulley'),
    slots : {},
  }

  return {
    mesh  : getRackModel('FM_SHARED', 'bottom'),
    slots : {
      pulley : backPulley,
      beam   : backPost,
    },
  }
}

// MARK: Tread Plate
function createRhinoTreadPlate(context: RhinoContext): PartDefinition {
  const { config } = context
  const useIndyPlate = config.treadPlate || config.addIndy

  return {
    mesh: useIndyPlate
      ? getRackModel('RHINO_PARTS', 'indy tread plate')
      : getRackModel('RHINO_PARTS', 'tread plate'),
    slots: {},
  }
}

// MARK: Low Row
function createRhinoLowRow(): PartDefinition {
  return {
    mesh  : getRackModel('RHINO_PARTS', 'low row'),
    slots : {},
  }
}

// MARK: Attachments
export function createRhinoLatBarClip(config: RackOptions): PartDefinition {
  const latBarAttachment: PartDefinition = {
    mesh      : `${ATTACHMENTS}#${config.latBarSelection}`,
    slots     : {},
    materials : config.materials?.latpulldown,
  }

  return {
    mesh  : `${ATTACHMENTS}#clip`,
    slots : { bottom: config.latBarSelection ? [latBarAttachment] : [] },
  }
}

// MARK: Depth Helper
export function getRhinoDepth(config: RackOptions): string {
  if (!config.sixPost) {
    if (config.depth === '4') {
      return '43'
    } else if (config.depth === '3') {
      return '30'
    }
  }

  return '24'
}
